import React, { useContext, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HighRiskContext } from "../../../context/HighRiskContext";
import {
  CircularProgress,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";
import { formatDateForTable } from "../../../common/helpers";

// Los 3 toggles ppvEr/lace/bhStatus son estados ternarios en la UI:
// "" = no filtrar (null en API), "true" y "false".
const FLAG_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const flagValueToBool = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  return value === "true" || value === true;
};

const HighRiskTable = ({ selectedLocation, setSelectedLocation }) => {
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { highRiskPatients, loading, error, applyFilters, resetFilters } =
    useContext(HighRiskContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  // Drafts locales — los filtros NO se aplican hasta que el usuario haga click
  // en Search. Así se evita re-fetch en cada keystroke / cambio de campo.
  const [searchNameDraft, setSearchNameDraft] = useState("");
  const [cipIdDraft, setCipIdDraft] = useState("");
  const [dobDraft, setDobDraft] = useState("");
  const [ppvErDraft, setPpvErDraft] = useState("");
  const [laceDraft, setLaceDraft] = useState("");
  const [bhStatusDraft, setBhStatusDraft] = useState("");
  const [includeInactiveDraft, setIncludeInactiveDraft] = useState(false);

  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleSearch = () => {
    applyFilters({
      searchName: searchNameDraft.trim() || null,
      cipId: cipIdDraft.trim() || null,
      dateOfBirth: dobDraft || null,
      locationId: selectedLocation === "" ? null : selectedLocation,
      ppvEr: flagValueToBool(ppvErDraft),
      lace: flagValueToBool(laceDraft),
      bhStatus: flagValueToBool(bhStatusDraft),
      onlyActive: !includeInactiveDraft,
    });
  };

  const handleReset = () => {
    setSearchNameDraft("");
    setCipIdDraft("");
    setDobDraft("");
    setPpvErDraft("");
    setLaceDraft("");
    setBhStatusDraft("");
    setIncludeInactiveDraft(false);
    setSelectedLocation("");
    resetFilters();
  };

  const handleViewPatient = (row) => {
    navigate(`/patient-info/${row.cipId}`, {
      state: {
        patientRow: {
          ...row,
          memberFullName: row.fullName,
          memberId: row.cipId,
          dob: row.dateOfBirth,
        },
        returnPath: location.pathname,
      },
    });
  };

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.cipId,
      getPatientName: (row) => row.fullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "flagsActive",
      headerName: "Flags",
      width: 100,
      renderCell: (params) => {
        const n = params.value ?? 0;
        const color = n >= 3 ? "error" : n === 2 ? "warning" : n === 1 ? "info" : "default";
        return <Chip size="small" label={`${n}/3`} color={color} />;
      },
    },
    { field: "cipId", headerName: "CIP ID", width: 140 },
    { field: "fullName", headerName: "Patient Name", flex: 2, minWidth: 260 },
    {
      field: "dateOfBirth",
      headerName: "DOB",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    { field: "gender", headerName: "Gender", width: 80 },
    {
      field: "ppvEr",
      headerName: "PPV-ER",
      width: 100,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="PPV-ER" color="error" /> : "—",
    },
    {
      field: "lace",
      headerName: "LACE",
      width: 90,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="LACE" color="warning" /> : "—",
    },
    {
      field: "bhStatus",
      headerName: "BH",
      width: 80,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="BH" color="secondary" /> : "—",
    },
    { field: "phoneNumber", headerName: "Phone", width: 150 },
    { field: "mobileNumber", headerName: "Mobile", width: 150 },
    { field: "memberEmail", headerName: "Email", flex: 1, minWidth: 200 },
    {
      field: "firstSeen",
      headerName: "First Seen",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "lastSeen",
      headerName: "Last Seen",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "status",
      headerName: "Active",
      width: 90,
      renderCell: (params) =>
        params.value === 1 ? (
          <Chip size="small" label="Active" color="success" />
        ) : (
          <Chip size="small" label="Inactive" />
        ),
    },
    { field: "medicalCenter", headerName: "Medical Center", flex: 1.2, minWidth: 180 },
    { field: "locationAddress", headerName: "Address", flex: 1.5, minWidth: 280 },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography color="error">Error fetching data</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filtros */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          px: 1,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Search by name"
          value={searchNameDraft}
          onChange={(e) => setSearchNameDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="CIP ID"
          value={cipIdDraft}
          onChange={(e) => setCipIdDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="DOB"
          InputLabelProps={{ shrink: true }}
          value={dobDraft}
          onChange={(e) => setDobDraft(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="high-risk-location-label">Location</InputLabel>
          <Select
            labelId="high-risk-location-label"
            value={selectedLocation}
            label="Location"
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-ppver-label">PPV-ER</InputLabel>
          <Select
            labelId="high-risk-ppver-label"
            value={ppvErDraft}
            label="PPV-ER"
            onChange={(e) => setPpvErDraft(e.target.value)}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-lace-label">LACE</InputLabel>
          <Select
            labelId="high-risk-lace-label"
            value={laceDraft}
            label="LACE"
            onChange={(e) => setLaceDraft(e.target.value)}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-bh-label">BH Status</InputLabel>
          <Select
            labelId="high-risk-bh-label"
            value={bhStatusDraft}
            label="BH Status"
            onChange={(e) => setBhStatusDraft(e.target.value)}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={includeInactiveDraft}
              onChange={(e) => setIncludeInactiveDraft(e.target.checked)}
            />
          }
          label="Include inactive"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
        >
          Reset
        </Button>
      </Box>

      {/* Tabla */}
      <Box ref={tableRef} sx={{ height: 560, width: "100%" }}>
        <DataGrid
          getRowId={(row) => row.highRiskId}
          rows={highRiskPatients}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.row.status !== 1 ? "row-inactive" : ""
          }
          sx={{
            "& .row-inactive": { backgroundColor: "#F4F6F8", color: "text.secondary" },
          }}
        />
      </Box>
    </>
  );
};

export default HighRiskTable;
